// Cookie consent management
// Categories: necessary (always on), analytics, marketing

export type ConsentCategories = {
  necessary: true
  analytics: boolean
  marketing: boolean
}

export type ConsentState = {
  categories: ConsentCategories
  timestamp: number
  version: number
}

const STORAGE_KEY = 'nd_cookie_consent'
const CONSENT_VERSION = 1
export const CONSENT_EVENT = 'nd-consent-change'
export const OPEN_SETTINGS_EVENT = 'nd-open-cookie-settings'

// Replace these with real production IDs when available
const GA_MEASUREMENT_ID = 'G-XXXXXXXXXX'
const META_PIXEL_ID = '000000000000000'

export const getConsent = (): ConsentState | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ConsentState
    if (parsed.version !== CONSENT_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

export const setConsent = (categories: Partial<ConsentCategories>) => {
  const state: ConsentState = {
    categories: {
      necessary: true,
      analytics: !!categories.analytics,
      marketing: !!categories.marketing,
    },
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }))
  applyTracking(state.categories)
  return state
}

export const acceptAll = () => setConsent({ analytics: true, marketing: true })

export const rejectAll = () =>
  setConsent({ analytics: false, marketing: false })

export const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT))
}

// --- Tracking script loaders ---
let gaLoaded = false
let metaLoaded = false

const loadGoogleAnalytics = () => {
  if (gaLoaded) return
  gaLoaded = true
  const w = window as any
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)
  w.dataLayer = w.dataLayer || []
  function gtag(...args: any[]) {
    w.dataLayer.push(args)
  }
  w.gtag = gtag
  gtag('js', new Date())
  gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true })
}

const loadMetaPixel = () => {
  if (metaLoaded) return
  metaLoaded = true
  const w = window as any
  ;(function (f: any, b: any, e: any, v: any) {
    if (f.fbq) return
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t = b.createElement(e)
    t.async = true
    t.src = v
    const s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(w, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  w.fbq('init', META_PIXEL_ID)
  w.fbq('track', 'PageView')
}

export const applyTracking = (categories: ConsentCategories) => {
  if (categories.analytics) loadGoogleAnalytics()
  if (categories.marketing) loadMetaPixel()
  // Note: scripts cannot be reliably "unloaded" once injected.
  // Users who revoke consent will need a page refresh; we still
  // stop new events by gating fbq/gtag calls behind getConsent().
}

export const initConsent = () => {
  const current = getConsent()
  if (current) applyTracking(current.categories)
}
